<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { CockpitLeaderboardEntry } from '@/types/cockpit'

const props = defineProps<{
  entries: CockpitLeaderboardEntry[] | null
  loading: boolean
  error: string | null
}>()

const emit = defineEmits<{
  retry: []
}>()

const { t, locale } = useI18n()
const router = useRouter()

function formatMoney(value: string, currency: string): string {
  const num = parseFloat(value)
  return new Intl.NumberFormat(locale.value, { style: 'currency', currency: currency.toUpperCase() }).format(num)
}

function formatPercent(value: number | null): string {
  if (value === null) return '—'
  return (value * 100).toFixed(2) + '%'
}

function navigateToOrg(id: number): void {
  void router.push({ name: 'admin.organisations.show', params: { id } })
}

const maxNetFee = computed(() => {
  if (!props.entries || props.entries.length === 0) return 0
  return Math.max(...props.entries.map((e) => parseFloat(e.net_fee)))
})

function barWidthPercent(netFee: string): number {
  if (maxNetFee.value === 0) return 0
  return (parseFloat(netFee) / maxNetFee.value) * 100
}
</script>

<template>
  <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6">
    <h2 class="text-lg font-bold text-white mb-6">
      {{ t('admin.cockpit.leaderboard.title') }}
    </h2>

    <!-- Loading skeletons -->
    <div v-if="loading" class="space-y-3">
      <div
        v-for="i in 5"
        :key="i"
        class="h-12 bg-slate-700 rounded-xl animate-pulse"
      />
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="bg-red-500/10 border border-red-500/30 rounded-xl p-5 flex items-center justify-between"
    >
      <p class="text-red-300 text-sm">{{ t(error) }}</p>
      <button
        type="button"
        class="text-sm text-red-300 hover:text-red-200 font-medium underline"
        @click="emit('retry')"
      >
        {{ t('admin.common.retry') }}
      </button>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!entries || entries.length === 0"
      class="text-center text-slate-500 py-10 text-sm"
    >
      {{ t('admin.cockpit.leaderboard.empty') }}
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-slate-400 text-xs uppercase tracking-wide border-b border-slate-700">
            <th scope="col" class="text-left pb-3 pr-4 font-medium">
              {{ t('admin.cockpit.leaderboard.col_organisation') }}
            </th>
            <th scope="col" class="text-right pb-3 pr-4 font-medium">
              {{ t('admin.cockpit.leaderboard.col_net_fee') }}
            </th>
            <th scope="col" class="text-right pb-3 pr-4 font-medium hidden md:table-cell">
              {{ t('admin.cockpit.leaderboard.col_gross_volume') }}
            </th>
            <!--th scope="col" class="text-right pb-3 pr-4 font-medium hidden lg:table-cell">
              {{ t('admin.cockpit.leaderboard.col_take_rate') }}
            </th-->
            <th scope="col" class="text-right pb-3 font-medium hidden lg:table-cell">
              {{ t('admin.cockpit.leaderboard.col_registrations') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-700/50">
          <tr
            v-for="(entry, idx) in entries"
            :key="entry.organisation_id"
            class="group"
          >
            <td class="py-3 pr-4">
              <div class="flex items-center gap-3">
                <span class="text-slate-500 text-xs w-5 shrink-0">{{ idx + 1 }}</span>
                <div class="flex-1 min-w-0">
                  <button
                    type="button"
                    class="text-white font-medium hover:text-emerald-400 transition-colors text-left truncate max-w-[200px]"
                    :title="entry.organisation_name"
                    @click="navigateToOrg(entry.organisation_id)"
                  >
                    {{ entry.organisation_name }}
                  </button>
                  <!-- Horizontal bar indicator -->
                  <div class="mt-1 h-1 bg-slate-700 rounded-full overflow-hidden w-full max-w-[160px]">
                    <div
                      class="h-1 bg-emerald-500 rounded-full transition-all"
                      :style="{ width: barWidthPercent(entry.net_fee) + '%' }"
                    />
                  </div>
                </div>
              </div>
            </td>
            <td class="py-3 pr-4 text-right">
              <span class="text-emerald-400 font-bold">
                {{ formatMoney(entry.net_fee, entry.currency) }}
              </span>
            </td>
            <td class="py-3 pr-4 text-right text-slate-300 hidden md:table-cell">
              {{ formatMoney(entry.gross_volume, entry.currency) }}
            </td>
            <!--td class="py-3 pr-4 text-right text-slate-400 hidden lg:table-cell">
              {{ formatPercent(entry.take_rate) }}
            </td-->
            <td class="py-3 text-right text-slate-400 hidden lg:table-cell">
              {{ entry.paid_registration_count.toLocaleString(locale) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
